import { useEffect, useMemo, useState } from 'react'
import {
  AlertCircle, Eye, FileText, FolderOpen, Loader2, RefreshCw, Trash2
} from 'lucide-react'
import { useTenant } from '@/hooks/useTenant'
import { getAuthToken } from '@/hooks/useAuth'
import KBFileUploader from './KBFileUploader'
import { API_BASE } from '@/services/api'

interface KBDocument {
  document_id: string
  name: string
  description: string
  file_type: string
  category: string
  status: string
  size_bytes: number
  created_at: string
  created_by: string
  is_active: boolean
}

interface DocumentContent {
  success: boolean
  filename: string
  file_id: string
  content: string
  size: number
  message?: string
}

interface UploadedFile {
  file_id: string
  filename: string
  size: number
}

interface KBDocumentExplorerProps {
  documents: KBDocument[]
  isLoading: boolean
  uploadsDisabled?: boolean
  onRefresh: () => void
  onDeleteRequest: (documentId: string) => void
}

export default function KBDocumentExplorer({
  documents,
  isLoading,
  uploadsDisabled,
  onRefresh,
  onDeleteRequest,
}: KBDocumentExplorerProps) {
  const { tenantId } = useTenant()
  const [selectedDocument, setSelectedDocument] = useState<KBDocument | null>(null)
  const [documentContent, setDocumentContent] = useState<DocumentContent | null>(null)
  const [contentLoading, setContentLoading] = useState(false)
  const [contentError, setContentError] = useState('')
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([])

  const getHeaders = async (): Promise<Record<string, string>> => {
    const token = await getAuthToken()
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'X-Tenant-Id': tenantId || '',
    }
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }
    return headers
  }

  useEffect(() => {
    if (selectedDocument && !documents.find(doc => doc.document_id === selectedDocument.document_id)) {
      setSelectedDocument(null)
      setDocumentContent(null)
    }
  }, [documents, selectedDocument])

  const handleDocumentClick = async (doc: KBDocument) => {
    setSelectedDocument(doc)
    setContentError('')
    setContentLoading(true)
    setDocumentContent(null)

    try {
      const headers = await getHeaders()
      const response = await fetch(
        `${API_BASE}/v1/knowledge-base/documents/${doc.document_id}?include_content=true`,
        { headers }
      )

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || 'Failed to load document content')
      }

      const data = await response.json()
      setDocumentContent(data)
    } catch (err: any) {
      setContentError(err.message || 'Error loading document content')
    } finally {
      setContentLoading(false)
    }
  }

  const handleFilesUploaded = (files: UploadedFile[]) => {
    setUploadedFiles(files)
    onRefresh()
  }

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    })
  }

  const readyDocuments = useMemo(
    () => documents.filter(doc => doc.status === 'ready'),
    [documents]
  )

  return (
    <div className="bg-white rounded-xl border border-gray-100 p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Document Explorer</h3>
          <p className="text-sm text-gray-500">
            Upload, browse, and preview the extracted text from your documents.
          </p>
        </div>
        <button
          onClick={onRefresh}
          disabled={isLoading}
          className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg disabled:opacity-50"
          title="Refresh"
        >
          <RefreshCw className={`w-5 h-5 ${isLoading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1 space-y-4">
          <KBFileUploader
            onFilesUploaded={handleFilesUploaded}
            uploadedFiles={uploadedFiles}
            disabled={uploadsDisabled}
          />
          {uploadsDisabled && (
            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
              Configure your OpenAI integration to upload new documents.
            </div>
          )}
        </div>

        <div className="lg:col-span-2 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
              <FolderOpen className="w-4 h-4 mr-2" />
              Documents ({readyDocuments.length})
            </h4>
            <div className="space-y-2 max-h-[460px] overflow-y-auto">
              {isLoading && (
                <div className="py-8 flex flex-col items-center justify-center space-y-4">
                  <div className="relative">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <FileText className="w-5 h-5 text-blue-500" />
                    </div>
                  </div>
                  <div className="text-center">
                    <p className="text-sm font-medium text-gray-700">Loading Documents</p>
                    <p className="text-xs text-gray-500 mt-1">Fetching your documents...</p>
                  </div>
                  <div className="flex gap-1">
                    <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                    <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                    <div className="w-2 h-2 bg-blue-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                  </div>
                </div>
              )}
              {!isLoading && readyDocuments.length === 0 && (
                <div className="text-center py-8 px-4">
                  <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center mx-auto mb-3">
                    <FolderOpen className="w-5 h-5 text-amber-500" />
                  </div>
                  <p className="text-sm font-medium text-gray-700">No documents yet</p>
                  <p className="text-xs text-gray-500 mt-1.5 max-w-[260px] mx-auto">
                    Upload FAQs, return policies, product guides, or shipping info to train your AI agents.
                  </p>
                </div>
              )}
              {!isLoading && readyDocuments.map(doc => (
                <div
                  key={doc.document_id}
                  onClick={() => handleDocumentClick(doc)}
                  className={`p-3 rounded-lg cursor-pointer transition-all flex items-center justify-between ${
                    selectedDocument?.document_id === doc.document_id
                      ? 'bg-yellow-500 text-gray-900'
                      : 'bg-gray-50 hover:bg-gray-100 text-gray-700'
                  }`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate">{doc.name}</div>
                    <div className="text-xs opacity-80">{doc.category} • {formatBytes(doc.size_bytes)}</div>
                    <div className="text-xs opacity-70">{formatDate(doc.created_at)}</div>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      onDeleteRequest(doc.document_id)
                    }}
                    className={`p-2 rounded-lg transition-colors ml-2 ${
                      selectedDocument?.document_id === doc.document_id
                        ? 'hover:bg-yellow-600 text-gray-900'
                        : 'hover:bg-red-50 text-red-500'
                    }`}
                    title="Delete document"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
              <Eye className="w-4 h-4 mr-2" />
              Preview
            </h4>
            <div className="bg-gray-50 rounded-lg p-4 max-h-[460px] overflow-y-auto">
              {!selectedDocument && (
                <div className="text-center py-8 text-gray-500 text-sm">
                  Select a document to preview the extracted text.
                </div>
              )}
              {contentLoading && (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 text-yellow-500 animate-spin" />
                </div>
              )}
              {contentError && (
                <div className="p-3 mb-3 bg-red-50 border border-red-200 rounded-lg flex items-center space-x-2">
                  <AlertCircle className="w-4 h-4 text-red-600" />
                  <span className="text-sm text-red-600">{contentError}</span>
                </div>
              )}
              {selectedDocument && !contentLoading && documentContent && (
                <div>
                  <div className="mb-4 pb-3 border-b border-gray-200">
                    <div className="flex items-center space-x-2">
                      <FileText className="w-4 h-4 text-gray-500" />
                      <h5 className="font-medium text-gray-900 text-sm">
                        {documentContent.filename || selectedDocument.name}
                      </h5>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      ID: <code className="bg-white px-1 py-0.5 rounded">{documentContent.file_id}</code>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      Size: {formatBytes(documentContent.size)}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      Category: {selectedDocument.category} • Type: {selectedDocument.file_type}
                    </div>
                  </div>

                  {documentContent.success ? (
                    <pre className="whitespace-pre-wrap break-words text-sm text-gray-700 bg-white p-4 rounded-lg border border-gray-100 max-h-64 overflow-y-auto">
                      {documentContent.content}
                    </pre>
                  ) : (
                    <div className="p-4 bg-yellow-50 text-yellow-700 rounded-lg text-sm text-center">
                      {documentContent.message || 'Cannot display content for this document.'}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
